/**
 * Portfolio Tracker service (Portfolio Tracker Module — Phase 1).
 *
 * Talks only to this app's own FastAPI backend (`/api/v1/portfolio/*`).
 * Every call is implicitly scoped to the current authenticated user by the
 * backend (via the JWT) — there is no `user_id` parameter anywhere here.
 * Reads are informational only, mirroring already-computed Indicator
 * Engine / canonical Breakout Score Engine data; nothing here calculates
 * anything.
 */

import api from "@/services/api";
import type { PortfolioHoldingRow, PortfolioListResponse, PortfolioSortField, SortOrder } from "@/types";

export interface PortfolioSearchParams {
  search?: string;
  page?: number;
  page_size?: number;
  // Filters holdings whose `portfolio_status` label exactly matches (e.g.
  // "Accumulate Zone") — powers the Portfolio Insights KPI-card
  // click-to-filter behavior. Bug fix (Portfolio Intelligence Phase 1):
  // this used to be sent as `status`, a query param the backend never
  // actually read, so the filter silently had no server-side effect.
  portfolio_status?: string;
  // Filters holdings whose `StockMaster.sector` exactly matches — powers
  // the Sector Intelligence section's click-to-filter behavior.
  sector?: string;
  // Filters holdings that currently carry this Market Signals code.
  signal?: "TOP_BREAKOUT" | "SHIFT_TO_BREAKOUT" | "RE_BREAKOUT_AFTER_FAILURE" | "RSI2_PULLBACK" | "VOLUME_OBV_BREAKOUT";
  // Omit both to get the backend's default sort (Breakout Score
  // highest-first, then Last Updated newest-first) — see
  // `PortfolioQueryService._sort`'s docstring.
  sort_by?: PortfolioSortField;
  sort_order?: SortOrder;
}

export interface AddHoldingBody {
  symbol: string;
  // Only consulted by the backend if `symbol` isn't already in Stock
  // Master (i.e. it's outside the seeded NIFTY 50 universe) — see
  // `StockDataImportService.get_or_create_stock`. Defaults to "NSE".
  exchange?: "NSE" | "BSE";
  quantity: string;
  average_buy_price: string;
}

export interface UpdateHoldingBody {
  quantity: string;
  average_buy_price: string;
}

export const portfolioService = {
  async list(params: PortfolioSearchParams = {}): Promise<PortfolioListResponse> {
    const { data } = await api.get<PortfolioListResponse>("/portfolio", { params });
    return data;
  },

  async addHolding(body: AddHoldingBody): Promise<PortfolioHoldingRow> {
    const { data } = await api.post<PortfolioHoldingRow>("/portfolio", body);
    return data;
  },

  async updateHolding(holdingId: string, body: UpdateHoldingBody): Promise<PortfolioHoldingRow> {
    const { data } = await api.patch<PortfolioHoldingRow>(`/portfolio/${holdingId}`, body);
    return data;
  },

  async deleteHolding(holdingId: string): Promise<void> {
    await api.delete(`/portfolio/${holdingId}`);
  },
};

export default portfolioService;
